// Product-group action hooks (S1 bulk lifecycle + S2 QR print) that the shared
// catalogue-queries foundation does not expose. They talk to the API only
// through the generated `client` (doctrine 3) and reuse the shared request
// helpers (fresh Idempotency-Key per mutation, RequestError normalisation).

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "../../../../../lib/api";
import { newIdempotencyKey, RequestError, unwrapVoid } from "../../../../../lib/request";

const PRODUCTS = ["catalogue", "products"] as const;

function idempotentHeader() {
  return { "Idempotency-Key": newIdempotencyKey() } as const;
}

export interface BulkLifecycleInput {
  ids: readonly string[];
  action: "activate" | "discontinue";
  /** Only for discontinue: force through an active-BOM reference (§5). */
  force?: boolean | undefined;
}

export interface BulkLifecycleResult {
  ok: number;
  failed: number;
  /** First error encountered, surfaced so the toolbar can show a request id. */
  firstError: RequestError | undefined;
}

/**
 * Bulk activate/discontinue for the S1 selection. The per-id foundation hooks
 * can't be called in a loop (rules of hooks), so this issues the calls itself,
 * each with its own Idempotency-Key, and reports an aggregate outcome. Partial
 * success is a real outcome (some ids may 403/409) — the caller renders it.
 */
export function useBulkLifecycle() {
  const qc = useQueryClient();
  return useMutation<BulkLifecycleResult, Error, BulkLifecycleInput>({
    mutationFn: async ({ ids, action, force }) => {
      let ok = 0;
      let failed = 0;
      let firstError: RequestError | undefined;
      for (const id of ids) {
        try {
          if (action === "activate") {
            unwrapVoid(
              await client.POST("/v1/catalogue/products/{id}/activate", {
                params: { path: { id }, header: idempotentHeader() },
              }),
              "bulk-activate",
            );
          } else {
            unwrapVoid(
              await client.POST("/v1/catalogue/products/{id}/discontinue", {
                params: { path: { id }, header: idempotentHeader() },
                body: force === true ? { force: true } : {},
              }),
              "bulk-discontinue",
            );
          }
          ok += 1;
        } catch (err) {
          failed += 1;
          if (firstError === undefined && err instanceof RequestError) firstError = err;
        }
      }
      return { ok, failed, firstError };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PRODUCTS }),
  });
}

/**
 * QR-card print for S2. The batch endpoint returns a PDF; PDF rendering is
 * DEFERRED to module 02's shared utility (spec §5/§12/§13) so today it answers
 * `501 NOT_IMPLEMENTED`. We call the real endpoint and surface whatever it
 * returns — a 501 becomes a RequestError the screen shows as an inline notice,
 * never a fabricated success.
 */
export function useQrPrint() {
  return useMutation({
    mutationFn: async (productIds: readonly string[]) => {
      unwrapVoid(
        await client.POST("/v1/catalogue/products/qr-print", {
          params: { header: idempotentHeader() },
          body: { product_ids: [...productIds] },
          parseAs: "blob",
        }),
        "qr-print",
      );
    },
  });
}
